import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { listReviews, exportUrl } from "../lib/cityPortal";
import { assetUrl, formatTypology } from "../lib/format";
import { Download, Search } from "lucide-react";
import { relativeDate } from "../lib/ownerUi";
import ListTable, { ListRow } from "../components/ListTable";

const FILTERS = [
  { v: "In Review", label: "Pending Review" },
  { v: "Approved", label: "Approved" },
  { v: "Rejected", label: "Rejected" },
  { v: "Needs Revision", label: "Needs Revision" },
  { v: "all", label: "All" },
];

const STATE_TONE = {
  "In Review": { fg: "#8a651f", bg: "rgba(185,138,60,0.14)" },
  Approved: { fg: "#4F6149", bg: "rgba(79,97,73,0.10)" },
  Rejected: { fg: "#A23B2E", bg: "rgba(162,59,46,0.10)" },
  "Needs Revision": { fg: "#1B1F4B", bg: "rgba(91,97,150,0.12)" },
  Draft: { fg: "#6C6B85", bg: "rgba(154,160,192,0.20)" },
};

export default function CityReviewQueue() {
  const { user } = useRole();
  const [status, setStatus] = useState("In Review");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    const t = setTimeout(() => {
      listReviews(user.id, { status, q }).then((d) => alive && setData(d));
    }, 200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [user?.id, status, q]);

  return (
    <section className="animate-fade-in-up" data-testid="city-reviews-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <span aria-hidden style={{ display: "inline-block", width: 28, height: 2, background: "#B14E2F" }} />
            <span className="wn-label" style={{ color: "#8F3C23" }}>City Staff · Design Review</span>
          </div>
          <h1
            className="mt-3"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(26px, 3.6vw, 32px)",
              fontWeight: 600,
              color: "#1B1F4B",
            }}
          >
            Design Review Queue.
          </h1>
          {data && (
            <p style={{ color: "#6C6B85", fontSize: 13, marginTop: 4 }}>
              <span className="tabular-nums" style={{ color: "#272636", fontWeight: 700 }}>{data.counts["In Review"]}</span>{" "}
              awaiting review · {" "}
              <span className="tabular-nums" style={{ color: "#272636", fontWeight: 700 }}>{data.counts["Approved"]}</span>{" "}
              approved · {" "}
              <span className="tabular-nums" style={{ color: "#272636", fontWeight: 700 }}>{data.counts["Rejected"]}</span>{" "}
              rejected · {" "}
              <span className="tabular-nums" style={{ color: "#272636", fontWeight: 700 }}>{data.counts["Needs Revision"]}</span>{" "}
              awaiting revisions.
            </p>
          )}
        </div>
        <a
          href={exportUrl("reviews", user?.id, { status, q })}
          className="wn-btn wn-btn-tertiary"
          data-testid="city-reviews-export"
        >
          <Download size={14} /> Export queue (CSV)
        </a>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap" data-testid="city-reviews-filters">
          {FILTERS.map((f) => {
            const active = status === f.v;
            return (
              <button
                key={f.v}
                onClick={() => setStatus(f.v)}
                data-testid={`city-reviews-filter-${f.v.toLowerCase().replace(/\s+/g, "-")}`}
                style={{
                  padding: "5px 12px",
                  borderRadius: 999,
                  border: "1px solid " + (active ? "#1B1F4B" : "#D9D8E4"),
                  background: active ? "#1B1F4B" : "transparent",
                  color: active ? "#FBFAFD" : "#272636",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 ml-auto" style={{ minWidth: 240 }}>
          <Search size={14} style={{ color: "#6C6B85" }} />
          <input
            className="wn-input"
            placeholder="Search design, architect, typology"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="city-reviews-search"
            style={{ flex: 1 }}
          />
        </div>
      </div>

      <div style={{ borderTop: "1px solid #D9D8E4", marginTop: 16 }} />

      <ListTable
        testId="city-reviews"
        columns={[
          { label: "Design" },
          { label: "Compliance preview" },
          { label: "Status" },
          { label: "Submitted", align: "right" },
        ]}
        gridTemplate={REV_GRID}
        gap={REV_GAP}
        items={data?.items}
        loadingHeight={86}
        emptyTitle="No designs in this filter."
        emptyBody="When architects submit new designs, they will land here for review."
        renderRow={(d, _i, isLast) => (
          <ReviewRow key={d.id} d={d} isLast={isLast} />
        )}
      />
    </section>
  );
}

/* Shared column template: Design (thumb + meta) · Compliance · Status · Submitted */
const REV_GRID = "minmax(0, 2fr) minmax(0, 1.6fr) 160px 130px";
const REV_GAP = 16;

function ReviewRow({ d, isLast }) {
  const tone = STATE_TONE[d.status] || STATE_TONE.Draft;
  const cp = d.compliance_preview || {};
  return (
    <ListRow
      as={Link}
      to={`/city/reviews/${d.id}`}
      gridTemplate={REV_GRID}
      gap={REV_GAP}
      isLast={isLast}
      data-testid={`city-reviews-row-${d.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 4,
            backgroundImage: `url(${assetUrl(d.exterior_photo)})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            flexShrink: 0,
          }}
          aria-hidden
        />
        <div className="min-w-0">
          <div
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 15,
              color: "#1B1F4B",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {d.name}
            <span
              style={{
                fontSize: 10,
                color: "#8F3C23",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontWeight: 700,
                marginLeft: 8,
              }}
            >
              {formatTypology(d.typology)}
            </span>
          </div>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 12,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {d.architect_firm || "—"}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap" style={{ gap: 4 }}>
        <ComplianceBadge ok={cp.zoning_ok} label="Zoning" />
        <ComplianceBadge ok={cp.permits_ok} label="Permits" />
        <ComplianceBadge
          warn
          ok={!cp.constraints_flagged}
          label={cp.constraints_flagged ? "Constraints" : "Constraints"}
        />
      </div>
      <span
        style={{
          padding: "3px 10px",
          borderRadius: 999,
          background: tone.bg,
          color: tone.fg,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
          justifySelf: "start",
        }}
      >
        {d.status}
      </span>
      <span
        className="tabular-nums"
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#6C6B85",
          fontSize: 12,
          textAlign: "right",
          whiteSpace: "nowrap",
        }}
      >
        {relativeDate(d.created_at || d.updated_at)}
      </span>
    </ListRow>
  );
}

function ComplianceBadge({ ok, warn, label }) {
  const color = ok ? "#4F6149" : warn ? "#8a651f" : "#A23B2E";
  return (
    <span
      style={{
        padding: "1px 7px",
        borderRadius: 999,
        border: `1px solid ${color}`,
        color,
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      }}
    >
      {ok ? "✓" : "!"} {label}
    </span>
  );
}
